import Button from '@pelckmans/elementis/components/button';
import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import { useToast } from '@pelckmans/elementis/components/toast';
import Upload from '@pelckmans/elementis/icons/upload';
import uniqBy from 'lodash/uniqBy';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../constants/analyticsEvents';
import { TOAST_ID } from '../../../../../constants/ToastIds';
import AccountContext from '../../../../../contexts/AccountContext';
import ModalContext from '../../../../../contexts/ModalContext';
import useAnalytics from '../../../../../hooks/useAnalytics';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import getXlsxExport, { ExportData } from '../../api/export';
import { useFilters } from '../../context/filter-context/FilterContext';
import { useResultsDataContext } from '../../context/ResultsDataContext';
import { ResultWithGroup } from '../per-student/hooks/useResults';
import SkoreExportModal from '../skore-export-modal/SkoreExportModal';
import { useTableOfContent } from '../TOCDataContext';

type Props = {
  results: ResultWithGroup[];
  xlsxData: ExportData;
  fetchData: () => Promise<void>;
};

function ExportButton({ results, xlsxData, fetchData }: Props) {
  const [loading, setLoading] = useState(false);
  const handleError = useThrowAsyncError();
  const { addToast } = useToast();
  const { showModal, closeModal } = useContext(ModalContext);
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const {
    account: { oauthIdentities },
  } = useContext(AccountContext);
  const userHasSmartschoolIdentity = oauthIdentities?.some(x => x.provider === 'smartschool');
  const { members } = useResultsDataContext();
  const { toc } = useTableOfContent();
  const { selectedGroups, selectedScorableItems, selectedSchool, selectedModule } = useFilters();
  const analytics = useAnalytics();

  const filteredResults = useMemo(() => {
    return results.filter(result => selectedScorableItems.some(item => item.id.split('-')?.[0] === result.exerciseId));
  }, [selectedScorableItems, results]);

  const hasPersonalGroup = useMemo(() => selectedGroups.some(group => !group.isSmartschoolSynced), [selectedGroups]);
  const hasMultipleScorableItems = selectedScorableItems.length !== 1;

  async function fetchXlsxExportData() {
    setLoading(true);

    try {
      await getXlsxExport(selectedModule!.id, xlsxData, language as 'nl' | 'fr');

      await analytics.capture({
        eventType: ANALYTICS_EVENT_TYPES.RESULTS_EXPORTED_XLSX,
        objectId: selectedModule!.id,
        subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_RESULTS,
      });
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }

  if (!userHasSmartschoolIdentity) {
    return (
      <Button
        prominence="secondary"
        size="sm"
        startIcon={<Upload data-testid="export-icon" />}
        onClick={() => fetchXlsxExportData()}
        busy={loading}
      >
        {t('PAGES.RESULTS.EXPORT.LABEL')}
      </Button>
    );
  }

  const uniqueScorableItems = uniqBy(
    selectedScorableItems,
    item => `${item.id}-${item.isAssignment}-${item.isFreeExercise}`,
  );

  const handleSmartschoolExport = () => {
    if (!selectedSchool || !selectedModule) return null;

    if (hasMultipleScorableItems) {
      addToast({
        id: TOAST_ID.MULTIPLE_SCORABLE_ITEMS,
        variant: 'warning',
        title: t('PAGES.RESULTS.MODALS.SMARTSCHOOL_SKORE_EXPORT.TOASTS.MULTIPLE_SCORABLE_ITEMS.TITLE'),
        children: t('PAGES.RESULTS.MODALS.SMARTSCHOOL_SKORE_EXPORT.TOASTS.MULTIPLE_SCORABLE_ITEMS.MESSAGE'),
      });
    }

    if (hasPersonalGroup) {
      addToast({
        id: TOAST_ID.PERSONAL_GROUP,
        variant: 'warning',
        title: t('PAGES.RESULTS.MODALS.SMARTSCHOOL_SKORE_EXPORT.TOASTS.PERSONAL_GROUP.TITLE'),
        children: t('PAGES.RESULTS.MODALS.SMARTSCHOOL_SKORE_EXPORT.TOASTS.PERSONAL_GROUP.MESSAGE'),
      });
    }

    if (hasMultipleScorableItems || hasPersonalGroup) return;

    const handleSuccess = async () => {
      await fetchData();

      closeModal('skore-export-modal');
      addToast({
        id: TOAST_ID.SKORE_EXPORT_SUCCESS,
        variant: 'success',
        title: t('PAGES.RESULTS.MODALS.SMARTSCHOOL_SKORE_EXPORT.TOASTS.SUCCESS.TITLE'),
        children: t('PAGES.RESULTS.MODALS.SMARTSCHOOL_SKORE_EXPORT.TOASTS.SUCCESS.MESSAGE'),
      });
    };

    showModal(
      <SkoreExportModal
        id="skore-export-modal"
        members={members}
        scorableItems={uniqueScorableItems}
        selectedGroups={selectedGroups}
        selectedSchool={selectedSchool}
        selectedModule={selectedModule}
        results={filteredResults}
        toc={toc}
        onSuccess={handleSuccess}
      />,
    );
  };

  return (
    <ButtonDropdown
      buttonProps={{ prominence: 'secondary', startIcon: <Upload data-testid="export-icon" /> }}
      size="sm"
      title={t('PAGES.RESULTS.EXPORT.LABEL')}
      busy={loading}
      disableBackgroundScroll
    >
      <ButtonDropdown.Item
        text={t('PAGES.RESULTS.EXPORT.BUTTON_DROPDOWN.XLSX')}
        onClick={() => fetchXlsxExportData()}
      />
      <ButtonDropdown.Item
        text={t('PAGES.RESULTS.EXPORT.BUTTON_DROPDOWN.SMARTSCHOOL')}
        onClick={handleSmartschoolExport}
      />
    </ButtonDropdown>
  );
}

export default ExportButton;
